import { BaseGameDrawer } from './BaseGameDrawer';
import {
  MOBILE_BONUS_CONTAINER_NAMES,
  MOBILE_MAIN_CONTAINER_NAMES,
  MOBILE_PRE_BONUS_CONTAINER_NAMES,
} from '../../core/mobileConstants';
import { GAME_MODES, GAME_SCENE } from '../../core/constats';
import { buyBonusMobilePopup } from '../../shared/buyBonusPopup/drawers/BuyBonusMobilePopup';
import { gameMobileConfig } from '../../core/configs';
import { backgroundDrawer } from '../../shared/background/BackgroundDrawer';
import { bonusWheelDrawer, wheelDrawer } from '../../shared/wheel/WheelDrawer';
import { gamePadDrawer } from '../../shared/gamePad/GamePadDrawer';
import { bonusBoardDrawer } from '../../shared/bonusBoard/BonusBoardDrawer';
import { oddCrystalsDrawer } from '../../shared/oddCrystals/OddCrystalsDrawer';
import { preBonusContentDrawer } from '../../shared/preBonusContent/PreBonusContentDrawer';
import { bonusPadDrawer } from '../../shared/bonusPad/BonusPadDrawer';
import { congratulationsMobilePopup } from '../../shared/congratulationsPopup/drawers/CongratulationsMobilePopup';
import { bigWinAnimationDrawer } from '../../shared/bigWinAnimation/BigWinAnimationDrawer';
import { loadingStageService } from '../../shared/loading/LoadingStageService';
import { bonusCongratulationsMobilePopup } from '../../shared/bonusCongratulationsPopup/drawers/BonusCongratulationsMobilePopup';

class GameMobileDrawer extends BaseGameDrawer {
  constructor(drawers) {
    super(drawers);
    this.once = new Set();

    this.config = {
      mainContainers: [
        { drawer: 'background', containerName: MOBILE_MAIN_CONTAINER_NAMES.BACKGROUND },
        { drawer: 'oddCrystal', containerName: MOBILE_MAIN_CONTAINER_NAMES.ODD_CRYSTALS },
        { drawer: 'bonusBoard', containerName: MOBILE_MAIN_CONTAINER_NAMES.BONUS_BOARD_CONTAINER },
        { drawer: 'gamePad', containerName: MOBILE_MAIN_CONTAINER_NAMES.GAMEPAD },
        { drawer: 'wheel', containerName: MOBILE_MAIN_CONTAINER_NAMES.WHEEL },
      ],
      preBonusContainers: [
        { drawer: 'background', containerName: MOBILE_PRE_BONUS_CONTAINER_NAMES.BACKGROUND },
        { drawer: 'gamePad', containerName: MOBILE_PRE_BONUS_CONTAINER_NAMES.GAMEPAD },
        { drawer: 'preBonus', containerName: MOBILE_PRE_BONUS_CONTAINER_NAMES.PRE_BONUS_ANIMATION },
      ],
      bonusContainers: [
        { drawer: 'background', containerName: MOBILE_BONUS_CONTAINER_NAMES.BACKGROUND },
        { drawer: 'bonusWheel', containerName: MOBILE_BONUS_CONTAINER_NAMES.WHEEL },
        { drawer: 'gamePad', containerName: MOBILE_BONUS_CONTAINER_NAMES.GAMEPAD },
        { drawer: 'bonusPad', containerName: MOBILE_BONUS_CONTAINER_NAMES.BONUS_PAD },
        { drawer: 'preBonus', containerName: MOBILE_BONUS_CONTAINER_NAMES.SCREEN_CHANGE_ANIMATION },
      ],
    };

    this.ressourceLoaded = null;
  }

  _setupScreenChangeAnimationContainer(content) {
    content.visible = false;
    const rect = this.createGraphics()
      .beginFill(0x000000)
      .drawRect(0, 0, gameMobileConfig.config.width, gameMobileConfig.config.height);
    this.setupButtonMode(rect);
    this.registerClick(rect, event => {
      event.stopPropagation();
    });
    rect.alpha = 0;
    content.addChild(rect);
  }

  _resetObserverOpenedState() {
    if (this.once.has('_resetObserverOpenedState')) {
      return;
    }
    const rect = this.createGraphics()
      .beginFill()
      .drawRect(0, 0, gameMobileConfig.config.width, gameMobileConfig.config.height)
      .endFill();
    this.parent.addChild(rect);
    rect.interactive = true;
    rect.buttonMode = false;
    rect.on('pointerdown', (event) => {
      event.stopPropagation();
      this.drawers.gamePad.resetOpenedState();
    });
    this.once.add('_resetObserverOpenedState');
  }

  defineMainSceneContainer() {
    this._defineContainers(
      this.config.mainContainers,
      this.getMainSceneContainer.bind(this),
    );
  }

  setupMainSceneContainer(parent) {
    this._setupSceneContainer({
      parent,
      containerNames: [
        MOBILE_MAIN_CONTAINER_NAMES.BACKGROUND,
        MOBILE_MAIN_CONTAINER_NAMES.ODD_CRYSTALS,
        MOBILE_MAIN_CONTAINER_NAMES.BONUS_BOARD_CONTAINER,
        MOBILE_MAIN_CONTAINER_NAMES.GAMEPAD,
        MOBILE_MAIN_CONTAINER_NAMES.WHEEL,
        MOBILE_MAIN_CONTAINER_NAMES.BUY_BONUS_POPUP,
        MOBILE_MAIN_CONTAINER_NAMES.SCREEN_CHANGE_ANIMATION,
        MOBILE_MAIN_CONTAINER_NAMES.BIG_WIN_ANIMATION,
        MOBILE_MAIN_CONTAINER_NAMES.CONGRATULATIONS_BONUS_POPUP,
      ],
      thisTarget: 'mainContainers',
    });
  }

  setupPreBonusSceneContainer(parent) {
    this._setupSceneContainer({
      containerNames: [
        MOBILE_PRE_BONUS_CONTAINER_NAMES.BACKGROUND,
        MOBILE_PRE_BONUS_CONTAINER_NAMES.GAMEPAD,
        MOBILE_PRE_BONUS_CONTAINER_NAMES.SCREEN_CHANGE_ANIMATION,
        MOBILE_PRE_BONUS_CONTAINER_NAMES.PRE_BONUS_ANIMATION,
      ],
      thisTarget: 'preBonusSceneContainers',
      parent,
    });
  }

  definePreBonusSceneContainer() {
    this._defineContainers(
      this.config.preBonusContainers,
      this.getPreBonusSceneContainer.bind(this),
    );
  }

  setupBonusSceneContainer(parent) {
    this._setupSceneContainer({
      containerNames: [
        MOBILE_BONUS_CONTAINER_NAMES.BACKGROUND,
        MOBILE_BONUS_CONTAINER_NAMES.GAMEPAD,
        MOBILE_BONUS_CONTAINER_NAMES.BONUS_PAD,
        MOBILE_BONUS_CONTAINER_NAMES.WHEEL,
        MOBILE_BONUS_CONTAINER_NAMES.GAME_PAD_FULL,
        MOBILE_BONUS_CONTAINER_NAMES.SCREEN_CHANGE_ANIMATION,
        MOBILE_BONUS_CONTAINER_NAMES.CONGRATULATIONS_BONUS_POPUP,
        MOBILE_BONUS_CONTAINER_NAMES.BIG_WIN_ANIMATION,
      ],
      thisTarget: 'bonusSceneContainers',
      parent,
    });
  }

  defineBonusSceneContainer() {
    this._defineContainers(
      this.config.bonusContainers,
      this.getBonusSceneContainer.bind(this),
    );
  }

  destroyMainSceneObservers() {
    this.drawers.background.mobileApplication.destroy();
    this.drawers.wheel.mobileApplication.destroy();
    this.drawers.oddCrystal.mobileApplication.destroy();
    this.drawers.gamePad.mobileApplication.destroy();
    this.drawers.bonusBoard.mobileApplication.destroy();
    this.drawers.buyBonusMobilePopup.destroy();
    this.drawers.bigWin.destroy();
    this.drawers.bonusCongratulationsPopup.destroy();
  }

  destroyPreBonusSceneObservers() {
    this.drawers.background.mobileApplication.destroy();
    this.drawers.gamePad.mobileApplication.destroy();
    this.drawers.preBonus.mobileApplication.destroy();
  }

  destroyBonusSceneObservers() {
    this.drawers.bonusWheel.mobileApplication.destroy();
    this.drawers.bonusPad.mobileApplication.destroy();
    this.drawers.background.mobileApplication.destroy();
    this.drawers.gamePad.mobileApplication.destroy();
    this.drawers.congratulationsMobilePopup.destroy();
    this.drawers.bigWin.destroy();
    this.drawers.gamePad.destroy();
  }

  executeMainScene() {
    this.executeBaseMainScene();
    this.getMainSceneContainer(MOBILE_MAIN_CONTAINER_NAMES.BUY_BONUS_POPUP).children = [];
    this.getMainSceneContainer(MOBILE_MAIN_CONTAINER_NAMES.BIG_WIN_ANIMATION).children = [];
    this.drawers.background.execute(GAME_MODES.MOBILE, { scene: GAME_SCENE.MAIN });
    this.drawers.wheel.execute(GAME_MODES.MOBILE, { scene: GAME_SCENE.MAIN });
    this.drawers.oddCrystal.execute(GAME_MODES.MOBILE, { scene: GAME_SCENE.MAIN });
    this.drawers.gamePad.execute(GAME_MODES.MOBILE, { scene: GAME_SCENE.MAIN });
    this.drawers.bonusBoard.execute(GAME_MODES.MOBILE, { scene: GAME_SCENE.MAIN });
    this.drawers.buyBonusMobilePopup.executeMobile({
      root: this.getMainSceneContainer(MOBILE_MAIN_CONTAINER_NAMES.BUY_BONUS_POPUP),
    });
    this.drawers.bigWin.execute(GAME_MODES.MOBILE, {
      root: this.getMainSceneContainer(MOBILE_MAIN_CONTAINER_NAMES.BIG_WIN_ANIMATION),
    });
    this.drawers.bonusCongratulationsPopup.execute({
      root: this.getMainSceneContainer(MOBILE_MAIN_CONTAINER_NAMES.CONGRATULATIONS_BONUS_POPUP),
    });
  }

  executePreBonusScene() {
    this.executeBasePreBonusScene();
    this.drawers.background.execute(GAME_MODES.MOBILE, { scene: GAME_SCENE.PRE_BONUS });
    this.drawers.gamePad.execute(GAME_MODES.MOBILE, { scene: GAME_SCENE.PRE_BONUS });
    this.drawers.preBonus.execute(GAME_MODES.MOBILE, { scene: GAME_SCENE.PRE_BONUS });
  }

  executeBonusScene() {
    this.executeBaseBonusScene();
    this.getBonusSceneContainer(MOBILE_BONUS_CONTAINER_NAMES.BIG_WIN_ANIMATION).children = [];
    this.getBonusSceneContainer(MOBILE_BONUS_CONTAINER_NAMES.CONGRATULATIONS_BONUS_POPUP).children = [];
    this.getBonusSceneContainer(MOBILE_BONUS_CONTAINER_NAMES.GAME_PAD_FULL).children = [];
    this.drawers.bonusWheel.execute(GAME_MODES.MOBILE, { scene: GAME_SCENE.BONUS });
    this.drawers.bonusPad.execute(GAME_MODES.MOBILE, { scene: GAME_SCENE.BONUS });
    this.drawers.background.execute(GAME_MODES.MOBILE, { scene: GAME_SCENE.BONUS });
    this.drawers.gamePad.execute(GAME_MODES.MOBILE, { scene: GAME_SCENE.BONUS });
    this.drawers.congratulationsMobilePopup.executeMobile({
      root: this.getBonusSceneContainer(MOBILE_BONUS_CONTAINER_NAMES.CONGRATULATIONS_BONUS_POPUP),
    });
    this.drawers.bigWin.execute(GAME_MODES.MOBILE, {
      root: this.getBonusSceneContainer(MOBILE_BONUS_CONTAINER_NAMES.BIG_WIN_ANIMATION),
    });
    this.drawers.gamePad.application.addBonusPressToStartLayer({
      root: this.getBonusSceneContainer(MOBILE_BONUS_CONTAINER_NAMES.GAME_PAD_FULL),
    });
  }

  async loadResource() {
    if (!this.ressourceLoaded) {
      this.ressourceLoaded = Promise.all([
        this.drawers.background.mobileApplication.onLoad().then(() => loadingStageService.next()),
        this.drawers.wheel.mobileApplication.onLoad().then(() => loadingStageService.next()),
        this.drawers.bonusWheel.mobileApplication.onLoad().then(() => loadingStageService.next()),
        this.drawers.gamePad.mobileApplication.onLoad().then(() => loadingStageService.next()),
        this.drawers.bonusBoard.mobileApplication.onLoad().then(() => loadingStageService.next()),
        this.drawers.oddCrystal.mobileApplication.onLoad().then(() => loadingStageService.next()),
        this.drawers.preBonus.mobileApplication.onLoad().then(() => loadingStageService.next()),
        this.drawers.bigWin.mobileApplication.onLoad().then(() => loadingStageService.next()),
        this.drawers.bonusPad.mobileApplication.onLoad().then(() => loadingStageService.next()),
        this.drawers.buyBonusMobilePopup.onLoad().then(() => loadingStageService.next()),
        this.drawers.congratulationsMobilePopup.onLoad().then(() => loadingStageService.next()),
      ]);
    }
    await this.ressourceLoaded;
  }
}

export const gameMobileDrawer = new GameMobileDrawer({
  background: backgroundDrawer, //shared
  wheel: wheelDrawer,
  bonusWheel: bonusWheelDrawer,
  gamePad: gamePadDrawer,
  bonusBoard: bonusBoardDrawer,
  oddCrystal: oddCrystalsDrawer,
  preBonus: preBonusContentDrawer,
  buyBonusMobilePopup: buyBonusMobilePopup,
  bonusPad: bonusPadDrawer, // bonusPage
  congratulationsMobilePopup: congratulationsMobilePopup,
  bigWin: bigWinAnimationDrawer,
  bonusCongratulationsPopup: bonusCongratulationsMobilePopup,
});
